import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Warning, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Self-serve account deletion (Apple Guideline 5.1.1(v)).
 * Soft-deletes the account on backend, clears cookies, signs the user out.
 */
export default function DeleteAccountModal({ open, onClose }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    if (submitting) return;
    setConfirm('');
    onClose();
  };

  const handleDelete = async () => {
    if (confirm.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/users/me`, {
        data: { confirm: 'DELETE' },
        withCredentials: true,
      });
      toast.success("Your account has been deleted. We're sorry to see you go.");
      try { await logout(); } catch { /* cookies were already cleared backend-side */ }
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete account');
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && close()}
          data-testid="delete-account-modal"
        >
          <motion.div
            initial={{ y: 100, scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 100, scale: 0.95 }}
            className="bg-[#0e0e16] border border-red-500/30 rounded-2xl w-full max-w-md overflow-hidden"
          >
            <div className="p-5 border-b border-red-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Warning size={20} weight="duotone" className="text-red-400" />
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Delete Account</h3>
              </div>
              <button onClick={close} className="text-[#666] hover:text-white" data-testid="delete-account-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-[#E4E4E7] text-sm leading-relaxed">
                This will permanently remove your profile, friends, message history, hosted events, match records, and any reviews you've written.
              </p>
              <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                <p className="text-red-400 text-xs font-semibold mb-1">This cannot be undone.</p>
                <p className="text-[#A1A1AA] text-[11px]">
                  If you change your mind within 30 days, email support@cyboard.games — we can sometimes restore your account.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                  Type <span className="text-white font-mono">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  className="input-field w-full text-sm font-mono"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>

            <div className="p-5 pt-2 flex gap-2 border-t border-white/[0.04]">
              <button onClick={close} className="btn-secondary flex-1" data-testid="delete-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting || confirm.trim().toUpperCase() !== 'DELETE'}
                className="flex-1 py-3 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="delete-confirm-btn"
              >
                {submitting ? 'Deleting…' : (<><Trash size={16} weight="duotone" /> Delete forever</>)}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
